import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { usePersistentCallback } from '../hooks';
import { usePreventScroll } from '../hooks/usePreventScroll';
import { Popup } from '../Popup';
import { DrawerContent } from './Content/Drawer-Content';
import { cnDrawer } from './Drawer.cn';
import type { IDrawerAnimationParams, IDrawerProps } from './Drawer.types';
import { useClientHeight, useSpring } from './Drawer.utils';

import './Drawer.css';

const defaultAnimation: IDrawerAnimationParams = {
    dragImmediate: false,
    disabled: false,
    tension: 230,
    friction: 24,
};

/**
 * Используется для создания шторки.
 * @param {IDrawerProps} props
 */
export const Drawer: FC<IDrawerProps> = (props) => {
    const {
        onOpenEnd: givenOnOpenEnd,
        className,
        visible,
        nested,
        direction = 'bottom',
        innerRef,
        preventScrollDisabled,
        preventScrollOnClose,
        animation = defaultAnimation,
        ariaHidden,
        initSpringDisabled = false,
        onClose,
    } = props;

    // прогресс открытия шторки от 0 до 1
    const [progress, setProgress] = useState(initSpringDisabled ? 1 : 0);

    // признак того, что анимация временно отключена (напр. на время drag жеста)
    const [springDisabled, setSpringDisabled] = useState(false);

    // спринговое значение прогресса и его производные
    const springImmediate = (animation.dragImmediate && springDisabled) || animation.disabled;
    const springValue = useSpring(progress, animation.tension, animation.friction, springImmediate);
    const springVisible = springValue > 0;

    const onOpenEnd = usePersistentCallback(givenOnOpenEnd);

    // Эмитит событие при открытии шторки
    useEffect(() => {
        if (springValue === 1 && onOpenEnd) {
            onOpenEnd();
        }
    }, [springValue, onOpenEnd]);

    // решает баг в iOS: в альбомной ориентации fixed элементы с
    // height: 100% показываются некорректно если виден navigation bar
    const clientHeight = useClientHeight();
    const popupStyle = useMemo(
        () => ({ height: clientHeight && clientHeight + 'px' }),
        [clientHeight],
    );

    const enabledPreventScroll = React.useMemo(() => {
        if (preventScrollDisabled) {
            return false;
        }

        return preventScrollOnClose ? springVisible : visible;
    }, [preventScrollDisabled, preventScrollOnClose, springVisible, visible]);

    usePreventScroll({ enabled: enabledPreventScroll });

    useEffect(() => {
        setSpringDisabled(false);
        setProgress(visible ? 1 : 0);
    }, [visible, animation]);

    const [closing, setClosing] = useState(false);

    const _onClose = useCallback((): void => {
        setClosing(true);
        onClose?.();
    }, [onClose]);

    return (
        <Popup
            className={cnDrawer({ visible: springVisible, direction, nested }, [className])}
            visible={springVisible}
            innerRef={innerRef}
            style={popupStyle}
            keepMounted={props.keepMounted}
            zIndex={props.zIndex}
            scope={props.scope}
            aria-hidden={ariaHidden}
            checkLastCloses
            onClose={_onClose}
            overlayCloseStrategy={'pressup'}
        >
            <DrawerContent
                {...props}
                closing={closing}
                setClosing={setClosing}
                onClose={_onClose}
                springValue={springValue}
                setProgress={setProgress}
                setSpringDisabled={setSpringDisabled}
            />
        </Popup>
    );
};

Drawer.displayName = cnDrawer();
